import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, DownloadIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAdminData } from '../../contexts/AdminDataContext';
import { downloadQuotePdf, quoteFileName, quotePdfDataUrl } from '../../utils/quotePdf';
import type { Quote } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
interface Props {
  quote: Quote;
  onClose: () => void;
}

/** Viser den færdige PDF — præcis samme fil som download og mailvedhæftning. */
export function QuotePdfPreview({
  quote,
  onClose
}: Props) {
  const {
    settings
  } = useAdminData();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    quotePdfDataUrl(quote, settings).then(next => {
      if (active) setUrl(next);
    }).catch(() => {
      if (active) setError('PDF’en kunne ikke dannes. Prøv igen.');
    });
    return () => {
      active = false;
    };
  }, [quote, settings]);
  return <div className="fixed inset-0 z-50 flex flex-col bg-ink/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="pdf-forhaandsvis-titel" data-id="element-135" data-mp="3ruhss1gldvjo">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease
    }} className="mx-auto flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-card bg-white" data-id="element-136" data-mp="5jlxsb3clme7">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-137" data-mp="jjwq7ikb391h">
          <div className="min-w-0" data-id="element-138" data-mp="ocg46gmwo66t">
            <h2 id="pdf-forhaandsvis-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-139" data-mp="odhq5g5gmd3b">
              Forhåndsvisning
            </h2>
            <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-140" data-mp="y7hsja9cx48c">{quoteFileName(quote.quoteNumber)}</p>
          </div>
          <div className="flex items-center gap-2" data-id="element-141" data-mp="2zbtqymqscfnz">
            <Button variant="secondary" size="sm" onClick={() => void downloadQuotePdf(quote, settings)} data-id="element-142" data-mp="2zclj1nl6bv9i">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-143" data-mp="2uead3ny024wk" />
              Download PDF
            </Button>
            <button type="button" onClick={onClose} aria-label="Luk forhåndsvisning" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-144" data-mp="3868dnadnash4">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-145" data-mp="3r3hxutqso9zf" />
            </button>
          </div>
        </header>

        <div className="flex-1 bg-canvas" data-id="element-146" data-mp="3n14qy20ba612">
          {error ? <p role="alert" className="flex items-center justify-center gap-2 p-10 text-sm text-danger" data-id="element-147" data-mp="1woyyh01jumlm">
              <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-148" data-mp="1snn4yjfk1h85" />
              {error}
            </p> : url ? <iframe title="Tilbud som PDF" src={url} className="h-full w-full" data-id="element-149" data-mp="1ikhsvpvit7vg" /> : <p className="flex items-center justify-center gap-2 p-10 text-sm text-ink-muted" data-id="element-150" data-mp="1mjz0aouicf39">
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-151" data-mp="18skfqdkyc5u3" />
              Danner PDF …
            </p>}
        </div>
      </motion.div>
    </div>;
}